import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, Phone, ShieldCheck, ArrowLeft, KeyRound } from "lucide-react";

function formatError(detail) {
  if (!detail) return "Une erreur est survenue. Reessayez.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : "")).filter(Boolean).join(" ");
  if (typeof detail === "object" && typeof detail.msg === "string") return detail.msg;
  return "Une erreur est survenue.";
}

/**
 * PhoneOtpModal — Africa's Talking SMS verification (Côte d'Ivoire only).
 *
 * Props:
 *   open: boolean
 *   onOpenChange: (boolean) => void
 *   onVerified: () => void  (called after a successful verification)
 *
 * Reads the current user via useAuth() for context (pre-fills the phone if any).
 */
export default function PhoneOtpModal({ open, onOpenChange, onVerified }) {
  const { user, refresh } = useAuth();
  const [step, setStep] = useState("phone"); // "phone" | "code"
  const [phone, setPhone] = useState("");
  const [normalisedPhone, setNormalisedPhone] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [cooldown, setCooldown] = useState(0); // seconds left before "renvoyer"
  const [devMode, setDevMode] = useState(false);
  const [emailAvailable, setEmailAvailable] = useState(false);
  const [sentVia, setSentVia] = useState("sms"); // sms | email
  const [emailHint, setEmailHint] = useState("");
  // Ref to read the raw input value at submit time — works around browsers
  // (some Android Chrome) where SMS autofill sets the value without firing
  // React's synthetic onChange, leaving state empty and the submit button
  // mysteriously disabled.
  const codeInputRef = useRef(null);

  // Pre-fill phone from user's existing number if present
  useEffect(() => {
    if (open && user) {
      setPhone((p) => p || user.phone_e164 || user.phone || "");
    }
  }, [open, user]);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setStep("phone"); setCode(""); setError(""); setLoading(false); setCooldown(0); setDevMode(false);
    }
  }, [open]);

  // Cooldown countdown
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const sendOtp = async (e, channel = "sms") => {
    if (e && e.preventDefault) e.preventDefault();
    setError(""); setLoading(true);
    try {
      const res = await axios.post(
        `${API}/auth/send-otp`,
        { phone, channel },
        { withCredentials: true },
      );
      const data = res.data || {};
      if (data.already_verified) {
        toast.success("Numéro déjà vérifié");
        await refresh();
        if (onVerified) onVerified();
        onOpenChange(false);
        return;
      }
      setNormalisedPhone(data.phone || phone);
      setCooldown(data.cooldown_seconds || 60);
      setDevMode(!!data.dev_mode);
      setEmailAvailable(!!data.email_available);
      setSentVia(data.channel || "sms");
      setEmailHint(data.email_hint || "");
      setStep("code");
      toast.success(data.channel === "email" ? `Code envoyé par email à ${data.email_hint}` : "Code envoyé par SMS");
    } catch (err) {
      const msg = formatError(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (e) => {
    if (e) e.preventDefault();
    setError(""); setLoading(true);
    // Defensive read: SMS autofill on some Android browsers updates the DOM
    // input value without firing React onChange, so `code` state can be stale.
    // Fall back to the raw input value when state is incomplete.
    const liveValue = (codeInputRef.current?.value || "").replace(/\D/g, "").slice(0, 6);
    const effectiveCode = code.length === 6 ? code : liveValue;
    if (effectiveCode.length !== 6) {
      setLoading(false);
      const msg = "Entrez les 6 chiffres du code reçu par SMS.";
      setError(msg);
      toast.error(msg);
      return;
    }
    if (!normalisedPhone) {
      setLoading(false);
      const msg = "Le numéro est introuvable. Revenez en arrière et redemandez un code.";
      setError(msg);
      toast.error(msg);
      return;
    }
    try {
      await axios.post(
        `${API}/auth/verify-otp`,
        { phone: normalisedPhone, code: effectiveCode },
        { withCredentials: true },
      );
      toast.success("Numéro vérifié ✓");
      await refresh();
      if (onVerified) onVerified();
      onOpenChange(false);
    } catch (err) {
      // Network / CORS errors don't carry a response — surface something useful.
      const detail = err.response?.data?.detail;
      const msg = formatError(detail) || err.message || "Erreur réseau. Vérifiez votre connexion.";
      setError(msg);
      toast.error(msg);
      // If the server says the OTP record is gone, send the user back to step 1
      // so they can re-request a code with the right phone number.
      if (typeof detail === "string" && /expir|introuvable/i.test(detail)) {
        setStep("phone");
        setCode("");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md p-0 overflow-hidden bg-white rounded-3xl" data-testid="otp-modal">
        <div className="bg-gradient-to-br from-orange-50 to-white px-7 pt-7 pb-4 border-b border-orange-100">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[var(--ink)] flex items-center gap-2" data-testid="otp-modal-title">
              <ShieldCheck className="text-orange-600" size={22} />
              Vérification du téléphone
            </DialogTitle>
            <DialogDescription className="text-sm text-[var(--ink-soft)]">
              {step === "phone"
                ? "Pour publier en Côte d'Ivoire, nous devons vérifier votre numéro par SMS. Cela protège tout le monde contre la fraude."
                : `Entrez le code à 6 chiffres reçu par SMS sur ${normalisedPhone}.`}
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="px-7 pb-7 pt-5">
          {step === "phone" && (
            <form onSubmit={sendOtp} className="space-y-3" data-testid="otp-phone-form">
              <label className="block">
                <span className="text-xs font-semibold text-[var(--ink-soft)] uppercase tracking-wide">
                  Numéro Côte d'Ivoire
                </span>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-soft)]">
                    <Phone size={16} />
                  </span>
                  <input
                    type="tel"
                    inputMode="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="07 12 34 56 78 ou +225 07 12 34 56 78"
                    required
                    data-testid="otp-phone-input"
                    className="w-full !pl-10 !py-3 rounded-2xl border border-[var(--line)] focus:border-orange-400 focus:ring-2 focus:ring-orange-200 outline-none transition"
                  />
                </div>
                <p className="text-[11px] text-[var(--ink-soft)] mt-1">
                  Format CI : +225 suivi de 10 chiffres. SMS gratuit pour vous.
                </p>
              </label>

              {error && <p className="text-sm text-red-600 font-semibold" data-testid="otp-error">{error}</p>}

              <button
                type="submit"
                disabled={loading || !phone}
                className="btn-primary w-full !py-3 inline-flex items-center justify-center gap-2"
                data-testid="otp-send-btn"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Phone size={16} />}
                {loading ? "Envoi..." : "M'envoyer le code par SMS"}
              </button>
            </form>
          )}

          {step === "code" && (
            <form onSubmit={verifyOtp} className="space-y-3" data-testid="otp-code-form" noValidate>
              <label className="block">
                <span className="text-xs font-semibold text-[var(--ink-soft)] uppercase tracking-wide">
                  Code à 6 chiffres
                </span>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-soft)]">
                    <KeyRound size={16} />
                  </span>
                  <input
                    ref={codeInputRef}
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    onInput={(e) => {
                      // Belt-and-suspenders: some browsers (Android SMS autofill) fire
                      // onInput but skip React's synthetic onChange. Mirror to state.
                      const v = (e.target.value || "").replace(/\D/g, "").slice(0, 6);
                      if (v !== code) setCode(v);
                    }}
                    placeholder="123456"
                    required
                    autoFocus
                    data-testid="otp-code-input"
                    className="w-full !pl-10 !py-3 rounded-2xl border border-[var(--line)] focus:border-orange-400 focus:ring-2 focus:ring-orange-200 outline-none transition tracking-[0.4em] font-mono text-lg"
                  />
                </div>
                {devMode && (
                  <p className="text-[11px] text-orange-700 mt-1 font-semibold">
                    Mode développement : le code est affiché dans la console serveur (logs backend).
                  </p>
                )}
              </label>

              {error && <p className="text-sm text-red-600 font-semibold" data-testid="otp-error">{error}</p>}

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full !py-3 inline-flex items-center justify-center gap-2"
                data-testid="otp-verify-btn"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
                {loading ? "Vérification..." : "Vérifier le code"}
              </button>

              <div className="flex items-center justify-between pt-1">
                <button
                  type="button"
                  onClick={() => { setStep("phone"); setError(""); setCode(""); }}
                  className="text-xs text-[var(--ink-soft)] inline-flex items-center gap-1 hover:text-orange-600"
                  data-testid="otp-back-btn"
                >
                  <ArrowLeft size={12} /> Changer de numéro
                </button>
                <button
                  type="button"
                  onClick={() => sendOtp(null, "sms")}
                  disabled={cooldown > 0 || loading}
                  className="text-xs font-semibold text-orange-600 hover:underline disabled:opacity-50 disabled:no-underline disabled:cursor-not-allowed"
                  data-testid="otp-resend-btn"
                >
                  {cooldown > 0 ? `Renvoyer dans ${cooldown}s` : "Renvoyer le code"}
                </button>
              </div>
              {emailAvailable && sentVia !== "email" && (
                <button
                  type="button"
                  onClick={() => sendOtp(null, "email")}
                  disabled={cooldown > 0 || loading}
                  className="w-full mt-2 text-xs font-semibold text-[var(--ink-soft)] hover:text-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="otp-email-fallback-btn"
                >
                  {cooldown > 0 ? `SMS non reçu ? Recevoir le code par email (dans ${cooldown}s)` : "SMS non reçu ? Recevoir le code par email"}
                </button>
              )}
              {sentVia === "email" && (
                <p className="text-xs text-emerald-700 mt-2" data-testid="otp-email-sent-hint">
                  Code envoyé par email à {emailHint}. Vérifiez aussi vos spams.
                </p>
              )}
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
